// Single source of truth for the Experiences table columns. Both the table
// (which renders the columns) and the "Table fields" dropdown (which toggles
// them) read this list, so they can never drift out of sync.
//
// `removable: false` columns are always shown and can't be unchecked (the
// experience name). `defaultVisible` is the out-of-the-box column set. The
// array order is the order columns appear in the table.

export type ExperienceColumnKey =
  | "title"
  | "type"
  | "lastViewed"
  | "firstSeen"
  | "lastModified"
  | "views"
  | "playSessions"
  | "documentSessions"
  | "readDepth"
  | "chat"
  | "chatRate"
  | "videoCompletion"
  | "status";

export type ExperienceColumnConfig = {
  key: ExperienceColumnKey;
  labelKey: string;
  /** false = pinned column the user can't hide (experience name). */
  removable: boolean;
  /** Shown by default before the user customises anything. */
  defaultVisible: boolean;
};

// Max columns the user can show at once, so adding fields never overflows the
// table width. The pinned "title" column counts towards this budget.
export const MAX_VISIBLE_COLUMNS = 8;

export const EXPERIENCE_COLUMNS: ExperienceColumnConfig[] = [
  { key: "title", labelKey: "name", removable: false, defaultVisible: true },
  { key: "type", labelKey: "type", removable: true, defaultVisible: false },
  {
    key: "lastViewed",
    labelKey: "lastViewed",
    removable: true,
    defaultVisible: true,
  },
  {
    key: "firstSeen",
    labelKey: "firstSeen",
    removable: true,
    defaultVisible: false,
  },
  {
    key: "lastModified",
    labelKey: "lastModified",
    removable: true,
    defaultVisible: false,
  },
  { key: "views", labelKey: "views", removable: true, defaultVisible: true },
  {
    key: "playSessions",
    labelKey: "playSessions",
    removable: true,
    defaultVisible: false,
  },
  {
    key: "documentSessions",
    labelKey: "documentSessions",
    removable: true,
    defaultVisible: false,
  },
  {
    key: "readDepth",
    labelKey: "readDepth",
    removable: true,
    defaultVisible: true,
  },
  { key: "chat", labelKey: "chat", removable: true, defaultVisible: true },
  {
    key: "chatRate",
    labelKey: "chatRate",
    removable: true,
    defaultVisible: false,
  },
  {
    key: "videoCompletion",
    labelKey: "videoCompletion",
    removable: true,
    defaultVisible: true,
  },
  { key: "status", labelKey: "status", removable: true, defaultVisible: true },
];

export const DEFAULT_VISIBLE_COLUMNS: ExperienceColumnKey[] =
  EXPERIENCE_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);

const PINNED_COLUMNS: ExperienceColumnKey[] = EXPERIENCE_COLUMNS.filter(
  (c) => !c.removable
).map((c) => c.key);

const VALID_KEYS = new Set(EXPERIENCE_COLUMNS.map((c) => c.key));

export const normalizeVisibleColumns = (
  keys: ExperienceColumnKey[]
): ExperienceColumnKey[] => {
  const selected = new Set<ExperienceColumnKey>(
    keys.filter((k) => VALID_KEYS.has(k))
  );
  PINNED_COLUMNS.forEach((k) => selected.add(k));

  const ordered = EXPERIENCE_COLUMNS.map((c) => c.key).filter((k) =>
    selected.has(k)
  );

  if (ordered.length <= MAX_VISIBLE_COLUMNS) return ordered;
  const pinned = ordered.filter((k) => PINNED_COLUMNS.includes(k));
  const removable = ordered.filter((k) => !PINNED_COLUMNS.includes(k));
  return [...pinned, ...removable].slice(0, MAX_VISIBLE_COLUMNS);
};
